declare var google: any;
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AnimationOptions } from 'ngx-lottie';
import { TokenStorageService } from '../../services/token-storage.service';
import { environment } from '../../../environments/environment';
import { SnackbarService } from '../../../../myt/utils/services/snackbar.service';
import { AuthenticationService } from '../../services/authentication.service';
import { isPlatformBrowser } from '@angular/common';
import { UtilsService } from '../../services/utils.service';

@Component({
  selector: 'myt-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit {
  returnUrl = '/';
  authSection: 'login' | 'signup' = 'login';
  otpDigits = Array.from({ length: 6 }, () => '');
  heroOptions: AnimationOptions = {
    path: 'https://assets9.lottiefiles.com/packages/lf20_jcikwtux.json',
  };
  @ViewChild('googleButtonHost')
  private googleButtonHost?: ElementRef<HTMLDivElement>;

  googleButtonUnavailable = false;
  private isGoogleIdentityInitialized = false;
  private googleInitRetryCount = 0;
  private readonly googleInitRetryLimit = 20;

  constructor(
    private tokenStorageService: TokenStorageService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private auth: AuthenticationService,
    private utilsService: UtilsService,
    private readonly ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  email = new FormControl('', [Validators.required, Validators.email]);
  phone = new FormControl('', [
    Validators.required,
    Validators.pattern('[0-9 ]{10}'),
  ]);
  otp = new FormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(6),
    Validators.pattern(/^\d+$/),
  ]);
  errorMessage = '';
  isTermsAndConditionsSelected = false;
  isEmailLogin = true;
  isOtpTriggered = false;
  loading = false;

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    if (this.route.snapshot.queryParamMap.get('section') === 'signup') {
      this.authSection = 'signup';
    }

    let authenticated = false;
    if (this.auth.isAuthenticated) {
      const userData = this.tokenStorageService.loadUserFromLocalStorage();
      if (userData && userData.userId) {
        authenticated = true;
        this.utilsService.redirectUserToDashboard(this.returnUrl);
        return;
      }
    }

    if (!authenticated) {
      this.tokenStorageService.signOut();
      this.auth.isAuthenticated = false;
    }
  }

  ngAfterViewInit(): void {
    if (!this.auth.isAuthenticated) {
      this.initGoogleIdentity();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.authSection === 'login' && !this.isOtpTriggered) {
      this.renderGoogleButton();
    }
  }

  private get googleIdentity() {
    return typeof google !== 'undefined' ? google.accounts?.id : null;
  }

  private initGoogleIdentity() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const googleIdentity = this.googleIdentity;

    if (!googleIdentity) {
      if (this.googleInitRetryCount >= this.googleInitRetryLimit) {
        this.googleButtonUnavailable = true;
        return;
      }

      this.googleInitRetryCount += 1;
      window.setTimeout(() => this.initGoogleIdentity(), 250);
      return;
    }

    this.googleButtonUnavailable = false;
    this.googleInitRetryCount = 0;

    if (!this.isGoogleIdentityInitialized) {
      googleIdentity.initialize({
        client_id: environment.client_id,
        callback: (response: any) =>
          this.handleGoogleCredentialResponse(response),
      });
      this.isGoogleIdentityInitialized = true;
    }

    this.renderGoogleButton();
    this.promptGoogleOneTap();
  }

  private handleGoogleCredentialResponse(response: any) {
    // Google Identity Services' callback fires from a `<script>` that
    // isn't patched by zone.js, so the JS stack starts OUTSIDE Angular's
    // NgZone. `HttpClient`'s `.next` callback usually re-enters NgZone
    // via the XHR onload patch — but under certain microtask
    // interleavings it doesn't, and `router.navigateByUrl` then
    // completes without triggering change detection (URL flickers
    // back to /login). Re-enter NgZone explicitly so the whole
    // post-login chain — HTTP subscribe, `loginSuccess`,
    // `redirectUserToDashboard` — is guaranteed to run inside the
    // zone.
    this.ngZone.run(() => {
      const credential = response?.credential;

      if (!credential) {
        this.snackbarService.openSnackbar({
          message: 'Google sign-in could not be completed. Please try again.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      this.loading = true;
      this.auth.googleLogin(credential).subscribe({
        next: (apiResponse: any) => {
          if (
            apiResponse &&
            String(apiResponse.status).toUpperCase() === 'SUCCESS' &&
            apiResponse.data &&
            apiResponse.data.userId
          ) {
            this.auth.loginSuccess(apiResponse.data);
            this.navigateAfterLogin();
            return;
          }

          this.snackbarService.openSnackbar({
            message: this.getResponseMessage(
              apiResponse,
              'Google sign-in failed. Please try another method.'
            ),
            type: 'error',
            duration: 5000,
          });
        },
        error: (error: any) => {
          this.snackbarService.openSnackbar({
            message: this.getResponseMessage(
              error,
              'Google sign-in failed. Please try another method.'
            ),
            type: 'error',
            duration: 5000,
          });
        },
        complete: () => {
          this.loading = false;
        },
      });
    });
  }

  private renderGoogleButton() {
    const googleIdentity = this.googleIdentity;
    const googleButtonContainer = this.googleButtonHost?.nativeElement;

    if (!googleIdentity || !googleButtonContainer) {
      return;
    }

    const width = this.getGoogleButtonWidth(googleButtonContainer);

    googleButtonContainer.innerHTML = '';
    googleIdentity.renderButton(googleButtonContainer, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width,
    });
  }

  private getGoogleButtonWidth(container: HTMLElement) {
    const containerWidth =
      Math.floor(container.getBoundingClientRect().width) ||
      Math.floor(container.parentElement?.getBoundingClientRect().width ?? 0) ||
      320;

    return Math.max(220, Math.min(containerWidth, 380));
  }

  private promptGoogleOneTap() {
    if (
      !this.isGoogleIdentityInitialized ||
      this.authSection !== 'login' ||
      this.isOtpTriggered
    ) {
      return;
    }

    this.googleIdentity?.prompt();
  }

  private cancelGoogleOneTap() {
    this.googleIdentity?.cancel?.();
  }

  private navigateAfterLogin() {
    this.utilsService.redirectUserToDashboard(this.returnUrl);
  }

  updateErrorMessage() {
    if (this.email.hasError('required')) {
      this.errorMessage = 'You must enter a value';
    } else if (this.email.hasError('email')) {
      this.errorMessage = 'Not a valid email';
    } else {
      this.errorMessage = '';
    }
  }

  setAuthSection(section: 'login' | 'signup') {
    this.authSection = section;
    if (section === 'login') {
      this.isOtpTriggered = false;
      this.resetOtpDigits();
      setTimeout(() => {
        this.renderGoogleButton();
        this.promptGoogleOneTap();
      });
      return;
    }

    this.cancelGoogleOneTap();
  }

  canRequestOtp() {
    const currentControl = this.isEmailLogin ? this.email : this.phone;
    return currentControl.valid && this.isTermsAndConditionsSelected;
  }

  get loginTarget() {
    return this.isEmailLogin ? this.email.value : this.phone.value;
  }

  private getResponseMessage(
    response: any,
    fallbackMessage: string
  ): string {
    return (
      response?.error?.message ||
      response?.error?.errorResponse ||
      response?.message ||
      fallbackMessage
    );
  }

  private switchToSignup(message: string, type: 'warning' | 'info' | 'error' = 'warning') {
    this.authSection = 'signup';
    this.isOtpTriggered = false;
    this.otp.reset();
    this.resetOtpDigits();
    this.cancelGoogleOneTap();
    this.snackbarService.openSnackbar({
      message,
      type,
      duration: 5000,
    });
  }

  onOtpDigitInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = (input.value || '').replace(/\D/g, '').slice(-1);

    this.otpDigits[index] = value;
    input.value = value;
    this.syncOtpControl();

    if (value) {
      const nextInput = this.getOtpInputs(input)[index + 1];
      nextInput?.focus();
    }
  }

  onOtpKeyDown(index: number, event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !input.value && index > 0) {
      const previousInput = this.getOtpInputs(input)[index - 1];
      previousInput?.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedValue = event.clipboardData?.getData('text') ?? '';
    const digits = pastedValue.replace(/\D/g, '').slice(0, 6).split('');

    this.otpDigits = Array.from({ length: 6 }, (_, index) => digits[index] ?? '');
    this.syncOtpControl();

    const otpInputs = this.getOtpInputs(event.target as HTMLInputElement);
    otpInputs?.forEach((input, index) => {
      input.value = this.otpDigits[index] ?? '';
    });

    const focusIndex = Math.min(digits.length, 5);
    otpInputs?.[focusIndex]?.focus();
  }

  private syncOtpControl() {
    this.otp.setValue(this.otpDigits.join(''));
    this.otp.markAsDirty();
    this.otp.updateValueAndValidity({ emitEvent: false });
  }

  private getOtpInputs(input: HTMLInputElement) {
    return Array.from(
      input
        .closest('.auth-card__otp-row')
        ?.querySelectorAll<HTMLInputElement>('.auth-card__otp-input') ?? []
    );
  }

  resetOtpDigits() {
    this.otpDigits = Array.from({ length: 6 }, () => '');
  }

  triggerOtp() {
    if (this.isEmailLogin && this.email.valid && this.email.value) {
      this.auth.emailOtpLogin(this.email.value).subscribe({
        next: (response: any) => {
          if (response.status === 'Success' && response.message) {
            this.isOtpTriggered = true;
            this.resetOtpDigits();
            this.cancelGoogleOneTap();
            this.snackbarService.openSnackbar({
              message: `${response.message}`,
              type: 'success',
              duration: 5000,
            });
          } else {
            this.switchToSignup(
              this.getResponseMessage(
                response,
                'Login with OTP is unavailable for this email. Please sign up first.'
              ),
              'warning'
            );
          }
        },
        error: (error: any) => {
          this.switchToSignup(
            this.getResponseMessage(
              error,
              'Login with OTP is unavailable for this email. Please sign up first.'
            ),
            'warning'
          );
        },
      });
    }
    if (!this.isEmailLogin && this.phone.valid && this.phone.value) {
      this.auth.whatsappOtpLogin(this.phone.value).subscribe({
        next: (response: any) => {
          if (response.status === 'Success' && response.message) {
            this.isOtpTriggered = true;
            this.resetOtpDigits();
            this.cancelGoogleOneTap();
            this.snackbarService.openSnackbar({
              message: `${response.message}`,
              type: 'success',
              duration: 5000,
            });
          } else {
            this.switchToSignup(
              this.getResponseMessage(
                response,
                'Login with OTP is unavailable for this mobile number. Please sign up first.'
              ),
              'warning'
            );
          }
        },
        error: (error: any) => {
          this.switchToSignup(
            this.getResponseMessage(
              error,
              'Login with OTP is unavailable for this mobile number. Please sign up first.'
            ),
            'warning'
          );
        },
      });
    }
  }

  verifyOtp() {
    if (this.isEmailLogin && this.email.value && this.otp.value) {
      this.auth.validateEmailOtp(this.email.value, this.otp.value).subscribe({
        next: (response: any) => {
          if (response?.status === 'Success' && response?.data) {
            this.snackbarService.openSnackbar({
              message: `${response.message}`,
              type: 'success',
              duration: 5000,
            });
            this.auth.loginSuccess(response.data);
            this.isOtpTriggered = false;
            this.resetOtpDigits();
            this.navigateAfterLogin();
            return;
          }

          this.switchToSignup(
            this.getResponseMessage(
              response,
              'We could not verify this OTP. Please create an account first.'
            ),
            'warning'
          );
        },
        error: (error: any) => {
          this.switchToSignup(
            this.getResponseMessage(
              error,
              'We could not verify this OTP. Please create an account first.'
            ),
            'warning'
          );
        },
      });
    }
    if (!this.isEmailLogin && this.phone.value && this.otp.value) {
      this.auth.validateMobileOtp(this.phone.value, this.otp.value).subscribe({
        next: (response: any) => {
          if (response?.status === 'Success' && response?.data) {
            this.snackbarService.openSnackbar({
              message: `${response.message}`,
              type: 'success',
              duration: 5000,
            });
            this.auth.loginSuccess(response.data);
            this.isOtpTriggered = false;
            this.resetOtpDigits();
            this.navigateAfterLogin();
            return;
          }

          this.switchToSignup(
            this.getResponseMessage(
              response,
              'We could not verify this OTP. Please create an account first.'
            ),
            'warning'
          );
        },
        error: (error: any) => {
          this.switchToSignup(
            this.getResponseMessage(
              error,
              'We could not verify this OTP. Please create an account first.'
            ),
            'warning'
          );
        },
      });
    }
  }

  onOtpBlur() {}
}
